import React, { useCallback, useState } from 'react'
import MAlert from '@/utils/modal/MAlert'
import { HStack, VStack, Modal } from '@musinsa/mwms-ds'
import styled from 'styled-components'
import { borderRadiusSystem, colors } from '@/styles/theme'
import Text from '@/components/content/Text'
import Input from '@/components/content/Input'
import FieldLabel from '@/components/content/FieldLabel'
import RadioGroup from '@/components/content/RadioGroup'
import { Radio } from '@/components/content/Radio'
import { useCompleteExport, useGetExportCombo } from '@/services/excel/useExcelQuries'
import { ExcelDownloadType, ExportArea, ExportAudit } from '@/services/excel/excel.types'
import { REGEX } from '@/utils/consts'
import { Utils } from '@/utils/Utils'
import { AuthUtils } from '@/utils/AuthUtils'

interface Props {
  open: boolean
  /**
   * 데이터 보안 영역에 "개인정보"(포함/미포함, 기본 미포함) 항목을 노출한다 — 권한 등급 0 또는 MASTER 에게만.
   * 포함을 고른 경우에만 파일 비밀번호 입력 영역이 열리고 필수 검증이 걸린다.
   * 그 밖의 사용자는 항목 없이 "포함"으로 고정된다(비밀번호 필수).
   * 꺼져 있으면(기본) 종전처럼 파일 비밀번호만 필수로 받는다.
   */
  personalInfoOption?: boolean
  /** 파일 비밀번호를 받는다(기본). 개인정보 없는 다운로드는 끄고 사유만 받는다 — 1.0 ToolBar 와 같게 비밀번호는 개인정보 화면만. */
  withPassword?: boolean
  /** 감사로그 API_NM 영역(기본 LIST) */
  area?: ExportArea
  /** 감사로그 MENU_CD·탭 — 경로 끝이 화면코드가 아닌 화면만 */
  audit?: ExportAudit
  /** 서버가 엑셀을 만들고 다운로드 API 에 BE 감사(@AccessAudit)가 붙은 화면 — 공용 감사 API 를 부르지 않는다(두 번 남지 않게, LOGISPRD-15744). 사유는 onConfirm 으로 넘겨 다운로드 요청(바디·쿼리)에 싣는다. */
  serverAudit?: boolean
  onConfirm?: (params?: ExcelDownloadType) => void
  onClose: () => void
}

type PersonalInfoValue = 'Y' | 'N'

const PERSONAL_INFO_OPTIONS: { value: PersonalInfoValue; label: string }[] = [
  { value: 'Y', label: '포함' },
  { value: 'N', label: '미포함' },
]

function ExcelDownloadModal({
  open,
  personalInfoOption = false,
  withPassword = true,
  area,
  audit,
  serverAudit = false,
  onConfirm,
  onClose,
}: Props) {
  const [reasonCode, setReasonCode] = useState('')
  const [detailedReason, setDetailReason] = useState('')
  const [canExcludePersonalInfo] = useState(AuthUtils.isAuth0)
  const [personalInfo, setPersonalInfo] = useState<PersonalInfoValue>(canExcludePersonalInfo ? 'N' : 'Y')
  const [password, setPassword] = useState('')

  const { data: combo } = useGetExportCombo()
  const { mutate: logExport } = useCompleteExport(area, audit)

  // 옵션이 없으면 종전 동작(비밀번호 필수), 있으면 "포함" 일 때만 비밀번호를 받는다.
  const requirePassword = withPassword && (!personalInfoOption || personalInfo === 'Y')

  const handleConfirm = useCallback(() => {
    try {
      if (!reasonCode) return MAlert.show('사유코드를 선택해 주세요.')
      if (!detailedReason) return MAlert.show('상세사유를 입력해 주세요.')
      if (detailedReason.length < 5) return MAlert.show('상세사유는 5글자 이상 입력해 주세요.')
      if (requirePassword) {
        if (!password) return MAlert.show('파일 비밀번호를 입력해 주세요.')
        if (password.includes(' ')) return MAlert.show('파일 비밀번호에 공백을 사용할 수 없습니다.')
        if (!REGEX.password.test(password)) {
          return MAlert.show('비밀번호는 영문 대소문자/숫자/특수문자 조합, 8자 이상으로 설정하셔야 합니다.')
        }
      }

      if (onConfirm) {
        const params: ExcelDownloadType = { reasonCode, detailedReason }
        if (personalInfoOption) params.includePersonalInfo = personalInfo === 'Y'
        if (requirePassword) params.password = password
        onConfirm(params)
      }

      if (!serverAudit) logExport(`${reasonCode}_${detailedReason}`)
      onClose()
    } catch (e) {
      const defaultMessage = (e as Error).message ?? '엑셀 다운로드에 실패하였습니다. 다시 시도해주세요.'
      MAlert.show(Utils.getApiErrMessage(e, defaultMessage))
    }
  }, [reasonCode, detailedReason, password, personalInfoOption, personalInfo, requirePassword, serverAudit, onConfirm, onClose, logExport])

  return !open ? null : (
    <Modal open closeIcon size="md" title="엑셀 다운로드" type="confirm" onConfirm={handleConfirm} onClose={onClose}>
      <ModalContent gap={32}>
        <VStack gap={16}>
          <Text kind="Headline_16_bold">다운로드 사유</Text>
          <RadioGroupWrapper gap={16} p={16}>
            <HStack block align="center">
              <FieldLabel required content="사유코드" width={92} />
              {combo && (
                <RadioGroup block direction="row">
                  {combo?.DOWNLOAD_CD?.COMBO_OPT_CD?.map((code) => (
                    <Radio key={`grid-export-modal-${code}`} value={code} label={code} checked={reasonCode === code} onChange={setReasonCode} />
                  ))}
                </RadioGroup>
              )}
            </HStack>
            <Input
              block
              required
              size="lg"
              value={detailedReason}
              placeholder="사유를 입력하세요"
              label="상세사유"
              labelWidth={92}
              labelHeight={38}
              onChange={setDetailReason}
              helpText="5글자 이상 입력해주세요."
            />
          </RadioGroupWrapper>
        </VStack>
        {withPassword && (
          <VStack gap={16}>
            <Text kind="Headline_16_bold">데이터 보안</Text>
            <RadioGroupWrapper gap={16} p={16}>
              {personalInfoOption && canExcludePersonalInfo && (
                <HStack block align="center">
                  <FieldLabel required content="개인정보" width={92} />
                  <RadioGroup block direction="row">
                    {PERSONAL_INFO_OPTIONS.map(({ value, label }) => (
                      <Radio
                        key={`grid-export-modal-personal-info-${value}`}
                        value={value}
                        label={label}
                        checked={personalInfo === value}
                        onChange={(v) => setPersonalInfo(v as PersonalInfoValue)}
                      />
                    ))}
                  </RadioGroup>
                </HStack>
              )}
              {requirePassword && (
                <Input
                  block
                  required
                  size="lg"
                  value={password}
                  placeholder="비밀번호를 입력하세요"
                  label="파일 비밀번호"
                  labelWidth={92}
                  labelHeight={38}
                  onChange={setPassword}
                  helpText="8자리 이상, 대/소문자+숫자+특수 혼용, 공백 사용 불가"
                />
              )}
            </RadioGroupWrapper>
          </VStack>
        )}
      </ModalContent>
    </Modal>
  )
}

const RadioGroupWrapper = styled(VStack)`
  border-radius: ${borderRadiusSystem.md}px;
  background: ${colors.gray050};
`

const ModalContent = styled(VStack)`
  & * {
    text-align: start;
    justify-content: start;
  }
`

export default ExcelDownloadModal
